import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Input from "../../../component-lib/Atoms/Input/Input";
import { Formik } from "formik";
import { Form } from "react-router-dom";
import Button from "../../../component-lib/Molecules/button/Button";
import { TRIAGE_DATA } from "./constant";
import * as Yup from "yup";
import useDeviceType from "../../../custom-hooks/DeviceType";
import EventEHRRadioButton from "../../../component-lib/Molecules/radioButton/EventEHRRadioButton";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import { getLoginUserData } from "../../../utils/getToken";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import { formatDecimalNumber } from "../../../utils/decimalNumberFormatter";
import { eventEHRCustomToastMessage } from "../../../component-lib/Atoms/custom-toast-message/EventEHRCustomToastMessage";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    width: "708px", // Set your custom width here
    maxWidth: "none", // Prevents Material-UI from overriding your width
  },
}));

interface idsProps {
  visitId: string;
  patientId: string;
  typeOfCare?: string;
}

interface SaveTriageDialogProps {
  ids: idsProps;
  open: boolean;
  handleCloseDialog: () => void;
  handleTriageSubmittedData?: (data: any) => void;
}

export default function SaveTriageDialog({
  ids,
  open = false,
  handleCloseDialog,
  handleTriageSubmittedData = () => {},
}: SaveTriageDialogProps) {
  const loginUserData: any = getLoginUserData();
  const parsedToken = JSON.parse(loginUserData);

  const deviceType = useDeviceType();
  const [selectedCare, setSelectedCare] = React.useState("woundCare");
  const validationSchema = Yup.object().shape({
    // Triage vitals are all optional — staff may record only some of them
    // (or not repeat all on a follow-up reading).
    [TRIAGE_DATA?.TEMPERATURE]: Yup.string(),
    [TRIAGE_DATA.OXYGEN_SATURATION]: Yup.string().test(
      "oxygen-max-100",
      "Oxygen saturation must be 100 or less",
      (value) => !value || parseFloat(value) <= 100
    ),
    [TRIAGE_DATA.RESPIRATION_RATE]: Yup.string(),
    [TRIAGE_DATA.HEART_RATE]: Yup.string(),

    [TRIAGE_DATA.BLOOD_PRESSURE]: Yup.string().test(
      "systolic-higher-than-diastolic",
      "Systolic blood pressure must be higher than diastolic blood pressure",
      function (value) {
        const systolic = parseFloat(value || "");
        const diastolic = parseFloat(this.parent[TRIAGE_DATA.BLOOD_PRESSURE2]);
        if (!systolic || !diastolic) return true; // Skip if either is missing
        return systolic > diastolic;
      }
    ),
    [TRIAGE_DATA.BLOOD_PRESSURE2]: Yup.string().test(
      "diastolic-lower-than-systolic",
      "Diastolic blood pressure must be lower than systolic blood pressure",
      function (value) {
        const diastolic = parseFloat(value || "");
        const systolic = parseFloat(this.parent[TRIAGE_DATA.BLOOD_PRESSURE]);
        if (!systolic || !diastolic) return true; // Skip if either is missing
        return diastolic < systolic;
      }
    ),
    // [TRIAGE_DATA.CARE_VALUE]: Yup.string().required("This field is required"),
    // [TRIAGE_DATA.TRIAGE_TAKEN_LOCATION]: Yup.string().required(
    //   "Triage taken location is required"
    // ),
  });

  const handleClose = () => {
    handleCloseDialog();
  };

  return (
    <React.Fragment>
      <BootstrapDialog
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <Formik
          initialValues={{
            [TRIAGE_DATA.TEMPERATURE]: "",
            [TRIAGE_DATA.OXYGEN_SATURATION]: "",
            [TRIAGE_DATA.RESPIRATION_RATE]: "",
            [TRIAGE_DATA.HEART_RATE]: "",
            [TRIAGE_DATA.BLOOD_PRESSURE]: "",
            [TRIAGE_DATA.BLOOD_PRESSURE2]: "",
            // [TRIAGE_DATA.CARE]: "woundCare",
            // [TRIAGE_DATA.CARE_VALUE]: "",
            [TRIAGE_DATA.TRIAGE_TAKEN_BY]: `${parsedToken?.name}`,
            // [TRIAGE_DATA.TRIAGE_TAKEN_BY_ID]: `${parsedToken?.customAttributes?.userId}`,
            // [TRIAGE_DATA.TRIAGE_TAKEN_LOCATION]: "",
          }}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={(values, { setSubmitting }) => {
            try {
              // Vitals are optional, but require at least one to be entered.
              const hasAnyVital = [
                TRIAGE_DATA.TEMPERATURE,
                TRIAGE_DATA.OXYGEN_SATURATION,
                TRIAGE_DATA.RESPIRATION_RATE,
                TRIAGE_DATA.HEART_RATE,
                TRIAGE_DATA.BLOOD_PRESSURE,
                TRIAGE_DATA.BLOOD_PRESSURE2,
              ].some((key) => String((values as any)?.[key] ?? "").trim() !== "");
              if (!hasAnyVital) {
                eventEHRCustomToastMessage({
                  status: "error",
                  title: "Please enter at least one vital sign",
                });
                return;
              }
              handleTriageSubmittedData(values);
              // handleOnSubmittedData && handleOnSubmittedData(values);
              // Handle form data here
              // If needed, call an API or further process the values here
            } catch (error) {
              console.log(error);
            } finally {
              setSubmitting(false); // Reset submitting state
            }
          }}
        >
          {({
            errors,
            values,
            touched,
            isValid,
            dirty,
            setFieldValue,
            handleBlur,
            handleSubmit,
          }) => {
            return (
              <Form
                onSubmit={handleSubmit}
                className="h-full flex flex-col justify-between"
              >
                <DialogTitle
                  sx={{ m: 0, p: 2, fontWeight: 600, fontSize: "18px" }}
                  id="customized-dialog-title"
                >
                  Vitals
                </DialogTitle>
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={(theme) => ({
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                  })}
                >
                  <CloseIcon />
                </IconButton>
                <hr />
                {/* MAIN CONTENT */}
                <DialogContent
                  sx={{
                    height: deviceType === "mobile" ? "50vh" : "fit-content",
                  }}
                >
                  <div>
                    <div className="mb-6">
                      <Input
                        label="Vitals Taken By"
                        disabled
                        type="string"
                        name={TRIAGE_DATA.TRIAGE_TAKEN_BY}
                        value={values[TRIAGE_DATA.TRIAGE_TAKEN_BY]}
                        onChangeCb={(e: any) => {
                          setFieldValue(
                            TRIAGE_DATA.TRIAGE_TAKEN_BY,
                            e.target.value
                          );
                        }}
                        onBlur={handleBlur}
                        size="small"
                        error={
                          touched[TRIAGE_DATA.TRIAGE_TAKEN_BY] &&
                          Boolean(errors[TRIAGE_DATA.TRIAGE_TAKEN_BY])
                        }
                        helperText={
                          touched[TRIAGE_DATA.TRIAGE_TAKEN_BY]
                            ? errors[TRIAGE_DATA.TRIAGE_TAKEN_BY]
                            : undefined
                        }
                      />
                    </div>

                    <div
                      style={{
                        padding: "0px 8px",
                        display: "grid",
                        gridTemplateColumns:
                          deviceType === "mobile" ? "1fr" : "1fr 1fr",
                        rowGap: "24px",
                        columnGap: "16px",
                      }}
                    >
                      {/* TEMPERATURE TEXTFIELD */}

                      <Input
                        label="Temperature"
                        endIcon={"°F"}
                        size="small"
                        type="text" // Use text to control input validation manually
                        inputMode="decimal"
                        isDecimal={true}
                        name={TRIAGE_DATA.TEMPERATURE}
                        value={values[TRIAGE_DATA.TEMPERATURE]}
                        onChangeCb={(e: any) => {
                          const formattedValue = formatDecimalNumber(
                            e.target.value
                          );
                          setFieldValue(
                            TRIAGE_DATA.TEMPERATURE,
                            formattedValue
                          );
                        }}
                        onBlur={handleBlur}
                        error={
                          touched[TRIAGE_DATA.TEMPERATURE] &&
                          Boolean(errors[TRIAGE_DATA.TEMPERATURE])
                        }
                        helperText={
                          touched[TRIAGE_DATA.TEMPERATURE]
                            ? errors[TRIAGE_DATA.TEMPERATURE]
                            : undefined
                        }
                      />

                      {/* OXYGEN SATURATION TEXTFIELD */}
                      <Input
                        label="Oxygen Saturation"
                        endIcon={"%"}
                        // type="text"
                        inputMode="decimal"
                        isDecimal={true}
                        name={TRIAGE_DATA.OXYGEN_SATURATION}
                        value={values[TRIAGE_DATA.OXYGEN_SATURATION]}
                        onChangeCb={(e: any) => {
                          const formattedValue = formatDecimalNumber(
                            e.target.value
                          );
                          setFieldValue(
                            TRIAGE_DATA.OXYGEN_SATURATION,
                            formattedValue
                          );
                        }}
                        onBlur={handleBlur}
                        size="small"
                        error={
                          touched[TRIAGE_DATA.OXYGEN_SATURATION] &&
                          Boolean(errors[TRIAGE_DATA.OXYGEN_SATURATION])
                        }
                        helperText={
                          touched[TRIAGE_DATA.OXYGEN_SATURATION]
                            ? errors[TRIAGE_DATA.OXYGEN_SATURATION]
                            : undefined
                        }
                      />
                      {/* HEART RATE TEXTFIELD */}

                      <Input
                        label="Heart Rate"
                        endIcon={"bpm"}
                        type="text"
                        inputMode="decimal"
                        isDecimal={true}
                        name={TRIAGE_DATA.HEART_RATE}
                        value={values[TRIAGE_DATA.HEART_RATE]}
                        onChangeCb={(e: any) => {
                          const formattedValue = formatDecimalNumber(
                            e.target.value
                          );
                          setFieldValue(TRIAGE_DATA.HEART_RATE, formattedValue);
                        }}
                        onBlur={handleBlur}
                        size="small"
                        error={
                          touched[TRIAGE_DATA.HEART_RATE] &&
                          Boolean(errors[TRIAGE_DATA.HEART_RATE])
                        }
                        helperText={
                          touched[TRIAGE_DATA.HEART_RATE]
                            ? errors[TRIAGE_DATA.HEART_RATE]
                            : undefined
                        }
                      />

                      {/* RESPIRATION RATE TEXTFIELD */}

                      <Input
                        label="Respiration Rate"
                        type="text"
                        inputMode="decimal"
                        isDecimal={true}
                        endIcon={"breath per minute"}
                        name={TRIAGE_DATA.RESPIRATION_RATE}
                        value={values[TRIAGE_DATA.RESPIRATION_RATE]}
                        onChangeCb={(e: any) => {
                          const formattedValue = formatDecimalNumber(
                            e.target.value
                          );
                          setFieldValue(
                            TRIAGE_DATA.RESPIRATION_RATE,
                            formattedValue
                          );
                        }}
                        onBlur={handleBlur}
                        size="small"
                        error={
                          touched[TRIAGE_DATA.RESPIRATION_RATE] &&
                          Boolean(errors[TRIAGE_DATA.RESPIRATION_RATE])
                        }
                        helperText={
                          touched[TRIAGE_DATA.RESPIRATION_RATE]
                            ? errors[TRIAGE_DATA.RESPIRATION_RATE]
                            : undefined
                        }
                      />
                      {/* BLOOD PRESSURE TEXTFIELD */}
                      <Input
                        label="Systolic"
                        placeholder="Systolic"
                        type="text"
                        inputMode="decimal"
                        isDecimal={true}
                        endIcon={"mmhg"}
                        name={TRIAGE_DATA.BLOOD_PRESSURE}
                        value={values[TRIAGE_DATA.BLOOD_PRESSURE]}
                        onChangeCb={(e: any) => {
                          const formattedValue = formatDecimalNumber(
                            e.target.value
                          );
                          setFieldValue(
                            TRIAGE_DATA.BLOOD_PRESSURE,
                            formattedValue
                          );
                        }}
                        onBlur={handleBlur}
                        size="small"
                        error={
                          touched[TRIAGE_DATA.BLOOD_PRESSURE] &&
                          Boolean(errors[TRIAGE_DATA.BLOOD_PRESSURE])
                        }
                        helperText={
                          touched[TRIAGE_DATA.BLOOD_PRESSURE]
                            ? errors[TRIAGE_DATA.BLOOD_PRESSURE]
                            : undefined
                        }
                      />
                      {/* BLOOD PRESSURE 2 TEXTFIELD */}
                      <Input
                        label="Diastolic"
                        placeholder="Diastolic"
                        type="text"
                        inputMode="decimal"
                        isDecimal={true}
                        endIcon={"mmhg"}
                        name={TRIAGE_DATA.BLOOD_PRESSURE2}
                        value={values[TRIAGE_DATA.BLOOD_PRESSURE2]}
                        onChangeCb={(e: any) => {
                          const formattedValue = formatDecimalNumber(
                            e.target.value
                          );
                          setFieldValue(
                            TRIAGE_DATA.BLOOD_PRESSURE2,
                            formattedValue
                          );
                        }}
                        onBlur={handleBlur}
                        size="small"
                        error={
                          touched[TRIAGE_DATA.BLOOD_PRESSURE2] &&
                          Boolean(errors[TRIAGE_DATA.BLOOD_PRESSURE2])
                        }
                        helperText={
                          touched[TRIAGE_DATA.BLOOD_PRESSURE2]
                            ? errors[TRIAGE_DATA.BLOOD_PRESSURE2]
                            : undefined
                        }
                      />
                    </div>
                    {/* <div className="ml-2 mr-2 mt-6 mb-4">
                      <EventEHRRadioButton
                        selectedValue={selectedCare}
                        name=""
                        labelCustomStyle={{
                          marginLeft: "10px",
                          color: "#565656",
                        }}
                        optionList={[
                          { label: "Wound Care", value: "woundCare" },
                          { label: "Dehydration", value: "dehydration" },
                          { label: "Injury", value: "injury" },
                          { label: "Other", value: "other" },
                        ]}
                        handleOnChange={(value: any) => {
                          setFieldValue(TRIAGE_DATA.CARE_VALUE, "");
                          setSelectedCare(value);
                          setFieldValue(TRIAGE_DATA.CARE, value);
                        }}
                      />
                    </div> */}
                    {/* <div>
                      {(selectedCare === "woundCare" ||
                        selectedCare === "dehydration" ||
                        selectedCare === "injury") && (
                        <Input
                          label={
                            selectedCare === "woundCare"
                              ? "Wound Severity"
                              : selectedCare === "dehydration"
                                ? "Hydration Level"
                                : selectedCare === "injury"
                                  ? "Injury Location"
                                  : ""
                          }
                          placeholder={
                            selectedCare === "woundCare"
                              ? "Enter Wound Severity"
                              : selectedCare === "dehydration"
                                ? "Enter Hydration Level"
                                : selectedCare === "injury"
                                  ? "Enter Injury Location"
                                  : "Enter Detail"
                          }
                          name={TRIAGE_DATA?.CARE_VALUE}
                          value={values[TRIAGE_DATA?.CARE_VALUE]}
                          onChangeCb={(e: any) => {
                            setFieldValue(
                              TRIAGE_DATA?.CARE_VALUE,
                              e.target.value
                            );
                          }}
                          onBlur={handleBlur}
                          size="small"
                          error={
                            touched[TRIAGE_DATA?.CARE_VALUE] &&
                            Boolean(errors[TRIAGE_DATA?.CARE_VALUE])
                          }
                          maxLength={100}
                          helperText={
                            touched[TRIAGE_DATA?.CARE_VALUE]
                              ? errors[TRIAGE_DATA?.CARE_VALUE]
                              : undefined
                          }
                        />
                      )}
                      {selectedCare === "other" && (
                        <Input
                          label="Detail"
                          placeholder="Enter Detail"
                          name={TRIAGE_DATA?.CARE_VALUE}
                          value={values[TRIAGE_DATA?.CARE_VALUE]}
                          onChangeCb={(e: any) => {
                            setFieldValue(
                              TRIAGE_DATA?.CARE_VALUE,
                              e.target.value
                            );
                          }}
                          onBlur={handleBlur}
                          size="small"
                          error={
                            touched[TRIAGE_DATA?.CARE_VALUE] &&
                            Boolean(errors[TRIAGE_DATA?.CARE_VALUE])
                          }
                          helperText={
                            touched[TRIAGE_DATA?.CARE_VALUE]
                              ? errors[TRIAGE_DATA?.CARE_VALUE]
                              : undefined
                          }
                        />
                      )}
                    </div> */}
                    {/* <div className="mt-6">
                      <Heading
                        title="Triage Taken Location"
                        customStyle={{
                          fontSize: "16px",
                          fontWeight: 500,
                          marginBottom: "12px",
                        }}
                      />
                      <SelectDropdown
                        // label="Triage Taken Location"
                        displayEmpty
                        placeholderValue="Select triage taken location"
                        name={TRIAGE_DATA.TRIAGE_TAKEN_LOCATION}
                        value={values[TRIAGE_DATA.TRIAGE_TAKEN_LOCATION]}
                        options={[
                          { label: "Clinic Location", value: "clinicLocation" },
                          { label: "JHC", value: "jhc" },
                        ]}
                        onChangeCb={(e) =>
                          setFieldValue(
                            TRIAGE_DATA.TRIAGE_TAKEN_LOCATION,
                            e.target.value
                          )
                        }
                        onBlur={handleBlur}
                        error={Boolean(
                          touched[TRIAGE_DATA.TRIAGE_TAKEN_LOCATION] &&
                            errors[TRIAGE_DATA.TRIAGE_TAKEN_LOCATION]
                        )}
                        helperText={
                          touched[TRIAGE_DATA.TRIAGE_TAKEN_LOCATION]
                            ? errors[TRIAGE_DATA.TRIAGE_TAKEN_LOCATION]
                            : undefined
                        }
                      />
                    </div> */}
                  </div>
                </DialogContent>
                {/* ACTION BUTTON */}
                <DialogActions style={{ padding: "24px" }}>
                  <Button
                    // disabled={!isValid || !dirty} // Disable button if form is empty or invalid
                    type="submit"
                    label={TEXT_CONSTANTS?.SAVE}
                    variant="contained"
                    onClickCb={() => {}}
                    style={{
                      padding: "8px 55px",
                      fontSize: "16px",
                    }}
                  />
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </BootstrapDialog>
    </React.Fragment>
  );
}
